/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    AUDIT_LOGGING_IMPLEMENTATION
 * Date:         2025-01-15
 * Modified By:  Cursor
 *
 * Description:
 * Component to display audit log history for a user account. Shows all admin actions
 * performed on the user including status changes, role changes, password resets, etc.
 *
 * Related Screens / Modules:
 * - client/src/services/auditLogService.ts
 * - client/src/features/admin/users/UserEditModal.tsx
 */

import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { AuditLog } from "../../../../interfaces/auditLog";
import * as auditLogService from "../../../../services/auditLogService";
import { formatDateTime } from "../../../../utils/dateFormat";
import { useUserCountry } from "../../../../hooks/useUserCountry";
import "./UserAuditLog.scss";
import AdminButton from "../../../../components/admin/AdminButton";
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_BUTTON_USAGE_AUDIT_LOG
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Replace react-bootstrap pagination buttons with AdminButton to align with
 * design-system button tokens.
 *
 * File Size Check:
 * - Current: 153
 * - Adding: ~10
 * - Projected: ~163
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .u-P2-16-medium (via btn-figma-1)
 * - Tokens used: --font-size-body-small, --line-height-body-small
 *
 * Related Files:
 * - client/src/components/admin/AdminButton.tsx
 * - client/src/features/admin/users/components/UserAuditLog.tsx
 */

interface UserAuditLogProps {
  userId: string;
  userName: string;
}

export const UserAuditLog: React.FC<UserAuditLogProps> = ({ userId, userName }) => {
  const country = useUserCountry();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);

  const fetchAuditLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await auditLogService.getUserAuditLogs(userId, page, 20);
      setLogs(response.logs);
      setTotal(response.total);
      setPages(response.pages);
    } catch (error) {
      console.error("Failed to load audit logs", error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, page]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      USER_CREATED: "User Created",
      USER_UPDATED: "User Updated",
      USER_DELETED: "User Deleted",
      STATUS_CHANGED: "Status Changed",
      ROLE_CHANGED: "Role Changed",
      PASSWORD_RESET: "Password Reset",
      EMAIL_VERIFIED: "Email Verified",
      BULK_DELETE: "Bulk Delete",
      BULK_STATUS_CHANGE: "Bulk Status Change",
      BULK_ROLE_CHANGE: "Bulk Role Change",
    };
    return labels[action] || action;
  };

  const getActionIcon = (action: string): string => {
    const icons: Record<string, string> = {
      USER_CREATED: "➕",
      USER_UPDATED: "✏️",
      USER_DELETED: "🗑️",
      STATUS_CHANGED: "🔄",
      ROLE_CHANGED: "👤",
      PASSWORD_RESET: "🔑",
      EMAIL_VERIFIED: "✉️",
    };
    return icons[action] || "📝";
  };

  const renderChangeDetails = (log: AuditLog) => {
    if (log.oldValue && log.newValue) {
      return (
        <div className="audit-change-details">
          <span className="old-value">{log.oldValue}</span>
          <span className="arrow">→</span>
          <span className="new-value">{log.newValue}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="user-audit-log">
      <div className="audit-log-header">
        <h3>Activity History for {userName}</h3>
        <p className="audit-log-count">{total} total actions</p>
      </div>

      {isLoading ? (
        <div className="audit-log-loading">
          <Spinner animation="border" role="status" size="sm" className="me-2" />
          Loading audit history...
        </div>
      ) : logs.length === 0 ? (
        <div className="audit-log-empty">No audit history available</div>
      ) : (
        <>
          <div className="audit-log-list">
            {logs.map((log) => (
              <div key={log._id} className="audit-log-item">
                <div className="audit-icon">{getActionIcon(log.action)}</div>

                <div className="audit-content">
                  <div className="audit-action">
                    <strong>{getActionLabel(log.action)}</strong>
                    {renderChangeDetails(log)}
                  </div>

                  <div className="audit-meta">
                    <span className="audit-admin">by {log.adminName}</span>
                    <span className="audit-date">{formatDateTime(log.createdAt, country)}</span>
                  </div>

                  {log.details && Object.keys(log.details).length > 0 && (
                    <div className="audit-details">
                      <pre>{JSON.stringify(log.details, null, 2)}</pre>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {pages > 1 && (
            <div className="audit-log-pagination">
              <AdminButton
                intent="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </AdminButton>
              <span>
                Page {page} of {pages}
              </span>
              <AdminButton
                intent="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page === pages}
              >
                Next
              </AdminButton>
            </div>
          )}
        </>
      )}
    </div>
  );
};
